import './ExampleBlock.scss';

import {
    Alert,
    Confirm,
    CustomDialog,
    ModalButton,
    ModalContent,
    ModalFooter,
    StaticDialog,
    useDialog,
} from '../../Modal';
import {
    AlertExampleCode,
    ConfirmExampleCode,
    CustomExampleCode,
    CustomStaticCode,
    StyleCode,
} from '../../Code';
import React, { ReactNode, useState } from 'react';

import { Button } from '../Button/Button';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface ExampleBlockProps {
    children?: ReactNode;
}

type CodeProps = {
    code: string;
    lang?: string;
};

function ExampleCodeBlock(props: CodeProps): JSX.Element {
    const [isMinimized, setMinimized] = useState(true);

    return (
        <div
            className={['code', isMinimized ? '--minimized' : ''].join(' ')}
            onClick={() => {
                if (isMinimized) {
                    setMinimized(false);
                }
            }}
        >
            {!isMinimized && (
                <div
                    className="codeHide"
                    onClick={() => {
                        setMinimized(true);
                    }}
                >
                    Hide source
                </div>
            )}
            <SyntaxHighlighter language={props.lang ?? 'tsx'} style={prism}>
                {props.code}
            </SyntaxHighlighter>
        </div>
    );
}

function AlertExample(): JSX.Element {
    const [alertState, setAlertState] = useState<string>();

    return (
        <div className="example">
            <div className="exampleLeft">
                <div className="exampleItem">
                    <Button
                        type="demo"
                        onClick={async () => {
                            setAlertState('Alert opened');

                            await Alert('Alert text', 'Alert title');

                            setAlertState('Alert closed');
                        }}
                    >
                        Alert
                    </Button>
                    <div className="exampleResult">{alertState}</div>
                </div>
            </div>
            <div className="exampleRight">
                <ExampleCodeBlock code={AlertExampleCode} />
            </div>
        </div>
    );
}

function ConfirmExample(): JSX.Element {
    const [alertState, setAlertState] = useState<string>();

    return (
        <div className="example">
            <div className="exampleLeft">
                <div className="exampleItem">
                    <Button
                        type="demo"
                        onClick={async () => {
                            setAlertState('Confirmation dialog is open');

                            const result = await Confirm('Сonfirmation text', 'Сonfirmation title');

                            setAlertState(result ? 'OK' : 'CANCEL');
                        }}
                    >
                        Confirm
                    </Button>
                    <div className="exampleResult">{alertState}</div>
                </div>
            </div>
            <div className="exampleRight">
                <ExampleCodeBlock code={ConfirmExampleCode} />
            </div>
        </div>
    );
}

export function CustomDialogContent(): JSX.Element {
    const dialog = useDialog();

    const [value, setValue] = useState<string>();

    return (
        <div className="customDialog">
            {/* <div style={{ height: '1000px', backgroundColor: 'red' }}></div> */}
            <ModalContent>
                <div>Custom dialog content</div>
                <label>
                    Input value:
                    <input
                        type="text"
                        autoFocus={true}
                        onChange={(e) => {
                            setValue(e.target.value);
                        }}
                    />
                </label>
            </ModalContent>
            <ModalFooter>
                <ModalButton
                    onClick={() => {
                        dialog.close(value);
                    }}
                >
                    Custom button
                </ModalButton>
            </ModalFooter>
        </div>
    );
}

function CustomExample(): JSX.Element {
    const [alertState, setAlertState] = useState<string>();

    return (
        <div className="example">
            <div className="exampleLeft">
                <div className="exampleItem">
                    <Button
                        type="demo"
                        onClick={async () => {
                            setAlertState('Custom dialog is open');

                            const result = await CustomDialog<string>(<CustomDialogContent />, {
                                title: 'Custom Dialog',
                                showCloseIcon: true,
                            });

                            setAlertState(result);
                        }}
                    >
                        Custom
                    </Button>
                    <div className="exampleResult">{alertState}</div>
                </div>
            </div>
            <div className="exampleRight">
                <ExampleCodeBlock code={CustomExampleCode} />
            </div>
        </div>
    );
}

function CustomStaticExample(): JSX.Element {
    const [isOpen, setOpen] = useState<boolean>(false);

    const [alertState, setAlertState] = useState<string>();

    return (
        <>
            <StaticDialog
                isOpen={isOpen}
                title="Custom static dialog"
                onAfterOpen={() => {
                    setAlertState('Custom dialog is open');
                }}
                showCloseIcon={true}
                onAfterClose={(result?: string) => {
                    setOpen(false);
                    setAlertState(result);
                }}
            >
                <CustomDialogContent />
            </StaticDialog>
            <div className="example">
                <div className="exampleLeft">
                    <div className="exampleItem">
                        <Button
                            type="demo"
                            onClick={async () => {
                                setOpen(true);
                            }}
                        >
                            Static
                        </Button>
                        <div className="exampleResult">{alertState}</div>
                    </div>
                </div>
                <div className="exampleRight">
                    <ExampleCodeBlock code={CustomStaticCode} />
                </div>
            </div>
        </>
    );
}

function AlertDesignExample(): JSX.Element {
    const [alertState, setAlertState] = useState<string>();

    const setStyle = () => {
        const root = document.documentElement;

        root.style.setProperty('--st-modal-borderRadius', '0px');
        root.style.setProperty('--st-modal-boxShadow', '0 10px 50px rgba(0, 0, 0, 0.8)');

        root.style.setProperty('--st-modal-backgroundColor', '#2a2a2a');
        root.style.setProperty('--st-modal-titleTextColor', '#eeeeee');
        root.style.setProperty('--st-modal-secondColor', '#3c3c3c');

        root.style.setProperty('--st-dialog-contentColor', '#fff');

        root.style.setProperty(
            '--st-modal-transformVisible',
            'translate3d(-50%, -50%, 0) scale(1)'
        );

        root.style.setProperty(
            '--st-modal-transformHidden',
            'translate3d(-50%, -50%, 0) scale(0.1)'
        );

        root.style.setProperty('--st-button-primaryColor', '#DDDDDD');
        root.style.setProperty('--st-button-primaryTextColor', '#000');
    };

    const resetStyle = () => {
        const root = document.documentElement;

        root.style.setProperty('--st-modal-borderRadius', '5px');
        root.style.setProperty('--st-modal-boxShadow', '0 0 10px rgba(0, 0, 0, 0.3)');

        root.style.setProperty('--st-modal-backgroundColor', '#ffffff');
        root.style.setProperty('--st-modal-titleTextColor', '#444444');
        root.style.setProperty('--st-modal-secondColor', '#f6f6f6');

        root.style.setProperty('--st-dialog-contentColor', '#000000');

        root.style.setProperty(
            '--st-modal-transformVisible',
            'translate3d(-50%, -50%, 0) scale(1) rotateX(0)'
        );
        root.style.setProperty(
            '--st-modal-transformHidden',
            'translate3d(-50%, 100%, 0) scale(0.5) rotateX(-90deg)'
        );

        root.style.setProperty('--st-button-primaryColor', '#5d9aff');
        root.style.setProperty('--st-button-primaryTextColor', '#ffffff');
    };

    return (
        <div className="example">
            <div className="exampleLeft">
                <div className="exampleItem">
                    <Button
                        type="demo"
                        onClick={async () => {
                            setStyle();
                            setAlertState('Alert opened');

                            await Alert('Alert text', 'Alert title');

                            setTimeout(() => {
                                resetStyle();
                            }, 500);
                            setAlertState('Alert closed');
                        }}
                    >
                        Custom Style
                    </Button>
                    <div className="exampleResult">{alertState}</div>
                </div>
            </div>
            <div className="exampleRight">
                <ExampleCodeBlock code={StyleCode} lang="scss" />
            </div>
        </div>
    );
}

export function ExampleBlock(props: ExampleBlockProps): JSX.Element {
    return (
        <div className="exampleBlock">
            <h2>Demos</h2>
            <AlertExample />
            <ConfirmExample />
            <CustomExample />
            <CustomStaticExample />
            <AlertDesignExample />
        </div>
    );
}
